import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { ROULETTE_CHIP_RATIO, ROULETTE_CHIP_RAW_VALUES, ROULETTE_CHIP_REAL_VALUES, ROULETTE_DEFAULT_CHIP_INDEX } from '@/config/original'
import type { RouletteBet, RouletteGameState, RoulettePlacedChip, RouletteRoundState } from '@/types/roulette'
import type { RootState } from '@/store/store'

export const initialRouletteState: RouletteGameState = {
	phase: 'ready',
	rouletteGameId: null,
	currencyCode: 'USD',
	selectedChipIndex: ROULETTE_DEFAULT_CHIP_INDEX,
	pendingChips: [],
	hoveredPlace: null,
	winningNumber: -1,
	winningColor: null,
	betResults: [],
	status: 'idle',
	isSubmitting: false,
	isWheelSpinning: false,
	isResultVisible: false,
	isClearing: false,
	currentMultiplier: 0,
	currentPayout: 0,
	currentProfit: 0,
	message: '',
}

function createPlacedChip(place: string, chipIndex: number): RoulettePlacedChip {
	const amount = ROULETTE_CHIP_REAL_VALUES[chipIndex] ?? 0
	return {
		id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}-${chipIndex}-${Math.random().toString(36).slice(2, 6)}`,
		place,
		chipIndex,
		amount,
		offsetX: Math.random() * 24 - 12,
		offsetY: Math.random() * 18 - 9,
		rotation: Math.random() * 50 - 25,
	}
}

/** Rebuild a place total as discrete chips (largest denominations first). */
function composeChipsForAmount(place: string, amount: number): RoulettePlacedChip[] {
	let remainingRaw = Math.round(amount * ROULETTE_CHIP_RATIO)
	if (remainingRaw <= 0) {
		return []
	}

	const chips: RoulettePlacedChip[] = []

	for (let index = ROULETTE_CHIP_RAW_VALUES.length - 1; index >= 0; index -= 1) {
		const raw = ROULETTE_CHIP_RAW_VALUES[index]
		const count = Math.floor(remainingRaw / raw)
		for (let i = 0; i < count; i += 1) {
			chips.push(createPlacedChip(place, index))
		}
		remainingRaw -= count * raw
	}

	return chips
}

export function aggregateRouletteChips(chips: RoulettePlacedChip[]): RouletteBet[] {
	const aggregated = new Map<string, number>()

	for (const chip of chips) {
		aggregated.set(chip.place, Math.round(((aggregated.get(chip.place) ?? 0) + chip.amount) * 1e8) / 1e8)
	}

	return [...aggregated.entries()].map(([place, amount]) => ({ place, amount }))
}

function clearSettledVisuals(state: RouletteGameState) {
	state.winningNumber = -1
	state.winningColor = null
	state.betResults = []
	state.status = 'idle'
	state.isResultVisible = false
	state.isWheelSpinning = false
	state.currentMultiplier = 0
	state.currentPayout = 0
	state.currentProfit = 0
	state.message = ''
	state.rouletteGameId = null
}

function applyRoundFields(state: RouletteGameState, data: RouletteRoundState) {
	state.winningNumber = typeof data.winningNumber === 'number' ? data.winningNumber : -1
	state.winningColor = data.winningColor
	state.betResults = Array.isArray(data.betResults) ? data.betResults : []
	state.currentMultiplier = data.multiplier
	state.currentPayout = data.payout
	state.currentProfit = data.profit
	state.rouletteGameId = data.rouletteGame?.id ?? null
	state.message = data.message ?? ''
	state.status = data.status
	state.isResultVisible = false

	if (data.rouletteGame?.currency_code) {
		state.currencyCode = data.rouletteGame.currency_code
	}
}

const rouletteSlice = createSlice({
	name: 'roulette',
	initialState: initialRouletteState,
	reducers: {
		resetRouletteGame(state) {
			return {
				...initialRouletteState,
				currencyCode: state.currencyCode,
				selectedChipIndex: state.selectedChipIndex,
			}
		},
		setRouletteCurrencyCode(state, action: PayloadAction<string>) {
			state.currencyCode = action.payload
		},
		setRouletteSelectedChipIndex(state, action: PayloadAction<number>) {
			const index = action.payload
			if (index < 0 || index >= ROULETTE_CHIP_REAL_VALUES.length) {
				return
			}
			state.selectedChipIndex = index
		},
		setRouletteSubmitting(state, action: PayloadAction<boolean>) {
			state.isSubmitting = action.payload
		},
		setRouletteClearing(state, action: PayloadAction<boolean>) {
			state.isClearing = action.payload
			if (action.payload) {
				state.isResultVisible = false
			}
		},
		setRouletteHoveredPlace(state, action: PayloadAction<string | null>) {
			state.hoveredPlace = action.payload
		},
		setRouletteWheelSpinning(state, action: PayloadAction<boolean>) {
			state.isWheelSpinning = action.payload
		},
		addRoulettePendingChip(state, action: PayloadAction<{ place: string; chipIndex: number }>) {
			if (state.phase !== 'ready' || state.isSubmitting || state.isClearing || state.isWheelSpinning || state.status === 'bet') {
				return
			}

			const chipIndex = action.payload.chipIndex
			const amount = ROULETTE_CHIP_REAL_VALUES[chipIndex] ?? 0
			if (!Number.isFinite(amount) || amount <= 0) {
				return
			}

			if (state.status === 'win' || state.status === 'lost' || state.status === 'draw') {
				clearSettledVisuals(state)
			}

			state.pendingChips.push(createPlacedChip(action.payload.place, chipIndex))
		},
		undoRoulettePendingChip(state) {
			if (state.phase !== 'ready' || state.isSubmitting || state.isClearing || state.isWheelSpinning || state.status === 'bet' || state.pendingChips.length === 0) {
				return
			}
			state.pendingChips.pop()
		},
		clearRoulettePendingChips(state) {
			if (state.phase !== 'ready' || state.isSubmitting || state.isClearing || state.isWheelSpinning || state.status === 'bet') {
				return
			}
			state.pendingChips = []
		},
		scaleRoulettePendingChips(state, action: PayloadAction<number>) {
			if (state.phase !== 'ready' || state.isSubmitting || state.isClearing || state.isWheelSpinning || state.status === 'bet' || state.pendingChips.length === 0) {
				return
			}

			const factor = action.payload
			if (!Number.isFinite(factor) || factor <= 0) {
				return
			}

			if (factor === 2) {
				const copies = state.pendingChips.map((chip) => createPlacedChip(chip.place, chip.chipIndex))
				state.pendingChips.push(...copies)
				return
			}

			if (factor === 0.5) {
				const groups = new Map<string, RoulettePlacedChip[]>()
				const order: string[] = []

				for (const chip of state.pendingChips) {
					const key = `${chip.place}:${chip.chipIndex}`
					if (!groups.has(key)) {
						groups.set(key, [])
						order.push(key)
					}
					groups.get(key)!.push(chip)
				}

				const next: RoulettePlacedChip[] = []
				for (const key of order) {
					const group = groups.get(key)!
					const keepCount = Math.floor(group.length / 2)
					const hasRemainder = group.length % 2 === 1

					next.push(...group.slice(0, keepCount))

					if (hasRemainder) {
						const sample = group[0]
						next.push(...composeChipsForAmount(sample.place, sample.amount / 2))
					}
				}

				state.pendingChips = next
				return
			}

			const scaledTotals = aggregateRouletteChips(state.pendingChips).map((bet) => ({
				place: bet.place,
				amount: Math.round(bet.amount * factor * 1e8) / 1e8,
			}))
			state.pendingChips = scaledTotals.flatMap((bet) => composeChipsForAmount(bet.place, bet.amount))
		},
		/** Apply an active start/active round (`status: "bet"`) and begin the spin path. */
		applyRouletteBetRound(state, action: PayloadAction<RouletteRoundState>) {
			const data = action.payload
			applyRoundFields(state, data)
			state.status = 'bet'
			state.phase = 'live'
			state.isSubmitting = false
			state.isResultVisible = false

			if (Array.isArray(data.bets) && data.bets.length > 0) {
				state.pendingChips = data.bets.flatMap((bet) => composeChipsForAmount(bet.place, bet.amount))
			}
		},
		applyRouletteSettledRound(state, action: PayloadAction<RouletteRoundState>) {
			const data = action.payload
			applyRoundFields(state, data)
			state.phase = 'live'
			state.isSubmitting = false
			state.isWheelSpinning = false
			state.isResultVisible = false
		},
		clearRoulettePendingAfterRound(state) {
			state.pendingChips = []
			state.phase = 'ready'
			state.isWheelSpinning = false
			state.isResultVisible = false
			state.hoveredPlace = null
			clearSettledVisuals(state)
		},
		showRouletteResult(state) {
			state.isResultVisible = true
		},
		dismissRouletteResult(state) {
			state.isResultVisible = false
		},
	},
})

export const {
	resetRouletteGame,
	setRouletteCurrencyCode,
	setRouletteSelectedChipIndex,
	setRouletteSubmitting,
	setRouletteClearing,
	setRouletteHoveredPlace,
	setRouletteWheelSpinning,
	addRoulettePendingChip,
	undoRoulettePendingChip,
	clearRoulettePendingChips,
	scaleRoulettePendingChips,
	applyRouletteBetRound,
	applyRouletteSettledRound,
	clearRoulettePendingAfterRound,
	showRouletteResult,
	dismissRouletteResult,
} = rouletteSlice.actions

export const selectRouletteGame = (state: RootState) => state.roulette

export default rouletteSlice.reducer
